import { LitElement, TemplateResult, html } from "lit";
import { property, state, queryAssignedNodes } from "lit/decorators.js";
import { ClassInfo, classMap } from "lit/directives/class-map.js";
import { Constructor } from "@slhuicore/base";
import { ITEM_CLICK } from "./item.constants";
import { SlhNavigationItemInterface } from "./item.interface";
import { style } from "./assets/item.generated-style";
import { SlhNavigationItemTypeEnum } from "./item.type";

const SlhBaseNavigationItem = <T extends Constructor<LitElement>>(
  superClass: T,
) => {
  class SlhNavigationItemElement extends superClass {
    /**
     * Used for setting id attribute value to the element, if not already set
     */
    static instanceCount = 0;

    /**
     * Navigation item URL
     */
    @property({
      type: String,
    })
    href: string = "";

    /**
     * Render navigation item in active state
     */
    @property({
      type: Boolean,
      attribute: true,
      reflect: true,
    })
    active: boolean = false;

    /**
     * Suppress link activation (useful when custom item click handling is required)
     */
    @property({
      type: Boolean,
      attribute: "prevent-default",
      reflect: true,
    })
    preventDefault: boolean = false;

    /**
     * Loading state with skeleton animation
     */
    @property({
      type: Boolean,
      attribute: true,
      reflect: true,
    })
    loading: boolean = false;

    /**
     * Organization item
     */
    @property({
      type: Boolean,
      attribute: true,
      reflect: true,
    })
    organization: boolean = false;

    /**
     * Show left border on active item
     */
    @property({
      type: Boolean,
      attribute: true,
      reflect: true,
    })
    prominent: boolean = false;

    /**
     * Item type
     */
    @property({
      type: String,
      attribute: true,
      reflect: true,
    })
    type: SlhNavigationItemTypeEnum | undefined = undefined;

    @state()
    protected activeState: boolean = false;

    @state()
    protected labelState: string = "";

    @state()
    protected connected: boolean = false;

    @queryAssignedNodes()
    protected defaultSlotNodes: Array<HTMLElement> | undefined;

    @queryAssignedNodes({ slot: "icon" })
    protected iconSlotNodes: Array<HTMLElement> | undefined;

    onKeyDown: (this: HTMLElement, e: KeyboardEvent) => any = () => {};

    static styles = style;

    /**
     * Set state on connected
     */
    connectedCallback() {
      super.connectedCallback();
      this.classList.add("item-wrapper");
      this.activeState = this.active;
      this.onKeyDown = this.onEnter.bind(this);
      this.addEventListener("keydown", this.onKeyDown);
    }

    disconnectedCallback(): void {
      this.removeEventListener("keydown", this.onKeyDown);
    }

    private onEnter(e: KeyboardEvent) {
      const { code } = e;
      if (code === "Enter" || code === "NumpadEnter") {
        this.click();
      }
    }

    /**
     * @returns {ClassInfo}
     */
    renderClasses(): ClassInfo {
      const hasIcon =
        this.iconSlotNodes !== undefined && this.iconSlotNodes?.length > 0;
      return {
        active: this.activeState,
        loading: this.loading,
        "has-icon": hasIcon,
        "organization-item": this.organization,
        prominent: this.prominent,
      };
    }

    /**
     * @fires "slhuinav:item-click"
     */
    click() {
      if (this.loading) {
        return;
      }
      const eventDetail = {
        label: this.labelState,
        url: this.href,
        active: this.activeState,
        id: this.id,
        type: this.type,
        href: this.href,
      };
      const customEvent = new CustomEvent(ITEM_CLICK, {
        detail: eventDetail,
        bubbles: true,
        composed: true,
        cancelable: true,
      });
      this.dispatchEvent(customEvent);

      if (this.href && !this.preventDefault) {
        location.href = this.href;
      } else {
        this.active = true;
      }
    }

    private setActive(value: boolean) {
      this.activeState = value;
      this.active = this.activeState;

      if (this.activeState) {
        this.setAttribute("aria-selected", "true");
      } else {
        this.removeAttribute("aria-selected");
      }
    }

    render(): TemplateResult {
      return html`<a
        @click="${this.click}"
        class="slh-nav-item ${classMap(this.renderClasses())}"
        href="${this.href}"
        tabindex="-1"
      >
        <slot name="icon"></slot>
        <slot></slot>
      </a>`;
    }

    requestUpdate(name?: PropertyKey, oldValue?: unknown) {
      if (
        name === "active" &&
        this.active !== this.activeState &&
        this.connected
      ) {
        this.setActive(this.active);
      }
      return super.requestUpdate(name, oldValue);
    }

    /**
     * Set text state, and update value if it's not defined
     */
    firstUpdated() {
      if (
        this.defaultSlotNodes !== undefined &&
        this.defaultSlotNodes.length > 0
      ) {
        this.labelState = this.sanitizeContent().trim();
        this.setAttribute("aria-label", this.labelState.trim());
      }
      if (!this.labelState) {
        console.error("[slh-nav-item] You must provide text slot to component");
      }

      if (!this.hasAttribute("id")) {
        this.id = `slh-nav-item-${(SlhNavigationItemElement.instanceCount += 1)}`;
      }
      this.setAttribute("role", "option");
      this.setAttribute("tabindex", "0");

      if (this.activeState) {
        this.setAttribute("aria-selected", "true");
      }

      if (this.active) {
        this.setActive(true);
      }
      this.connected = true;
    }

    /**
     * Strip html tags and join default nodes content
     */
    private sanitizeContent(): string {
      let textContent = ``;
      this.defaultSlotNodes?.forEach((element: HTMLElement) => {
        textContent += `${element.textContent}`;
      });
      return textContent.replace(/(<([^>]+)>)/gi, "");
    }
  }
  return SlhNavigationItemElement as unknown as Constructor<SlhNavigationItemInterface> &
    T;
};

export default SlhBaseNavigationItem;
